import { useState, useEffect, useRef } from 'react';
import ProductCard from '@/components/base/ProductCard';
import { products, categories, categoryGroups, FOOD_CATEGORIES } from '@/mocks/products';
import type { Product, Category } from '@/mocks/products';

interface CategoriesSectionProps {
  onInquiry: (product: Product) => void;
  defaultCategory?: string;
  categoryKey?: number;
}

const zhMap: Record<string, string> = {
  '탕류': '汤类',
  '해조류': '海藻类',
  '음료·두유': '饮料·豆乳',
  '차류': '茶类',
  '스낵·김': '零食·海苔',
  '생활용품': '生活用品',
  '김치·반찬': '泡菜·小菜',
  '스킨케어·뷰티': '护肤·美妆',
  '섬유향수': '衣物香水',
  '핸드크림': '护手霜',
  '운동화': '运动鞋',
  '건강용품': '健康用品',
  '식품': '食品',
};

// 스킨케어·뷰티 그룹에 속하는 세부 카테고리
const BEAUTY_CATEGORIES: Category[] = ['섬유향수', '핸드크림', '스킨케어·뷰티'];

export default function CategoriesSection({ onInquiry, defaultCategory, categoryKey }: CategoriesSectionProps) {
  const [activeGroup, setActiveGroup] = useState<string>('food');
  const [activeFoodSub, setActiveFoodSub] = useState<Category>('탕류');
  const [activeBeautySub, setActiveBeautySub] = useState<Category>('섬유향수');
  const [activeSingleCat, setActiveSingleCat] = useState<Category | null>(null);

  const [page, setPage] = useState(1);
  const INITIAL_SIZE = 12;
  const PAGE_SIZE = 12;

  const tabBarRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // defaultCategory prop 처리
  useEffect(() => {
    if (!defaultCategory) return;
    const cat = defaultCategory as Category;
    if (!categories.includes(cat)) return;

    if (FOOD_CATEGORIES.includes(cat)) {
      setActiveGroup('food');
      setActiveFoodSub(cat);
    } else if (BEAUTY_CATEGORIES.includes(cat)) {
      setActiveGroup('스킨케어·뷰티');
      setActiveBeautySub(cat);
      setActiveSingleCat(null);
    } else {
      setActiveGroup(cat);
      setActiveSingleCat(cat);
    }
    setPage(1);
  }, [defaultCategory, categoryKey]);

  // 현재 표시할 카테고리 결정
  const currentCategory: Category | null = (() => {
    if (activeGroup === 'food') return activeFoodSub;
    if (activeGroup === '스킨케어·뷰티') return activeBeautySub;
    return activeSingleCat;
  })();

  const filtered = currentCategory ? products.filter(p => p.category === currentCategory) : [];
  const displayed = filtered.slice(0, INITIAL_SIZE + PAGE_SIZE * (page - 1));
  const hasMore = displayed.length < filtered.length;

  const checkScroll = () => {
    const el = tabBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scrollTabs = (dir: 'left' | 'right') => {
    const el = tabBarRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const handleGroupClick = (groupId: string) => {
    setActiveGroup(groupId);
    setPage(1);
    if (groupId === 'food') {
      setActiveFoodSub('탕류');
      setActiveSingleCat(null);
    } else if (groupId === '스킨케어·뷰티') {
      setActiveBeautySub('섬유향수');
      setActiveSingleCat(null);
    } else {
      setActiveSingleCat(groupId as Category);
    }
  };

  const handleFoodSubClick = (cat: Category) => {
    setActiveFoodSub(cat);
    setPage(1);
  };

  const handleBeautySubClick = (cat: Category) => {
    setActiveBeautySub(cat);
    setPage(1);
  };

  const foodTotalCount = FOOD_CATEGORIES.reduce(
    (acc, cat) => acc + products.filter(p => p.category === cat).length,
    0
  );
  const beautyTotalCount = BEAUTY_CATEGORIES.reduce(
    (acc, cat) => acc + products.filter(p => p.category === cat).length,
    0
  );

  return (
    <section
      id="categories"
      className="py-16 md:py-20 px-4 md:px-8 lg:px-12"
      style={{ backgroundColor: '#F8F4EE', scrollMarginTop: 'calc(108px + var(--banner-height, 0px))' }}
    >
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: '#C4985A' }}>Categories</p>
          <h2 className="text-3xl md:text-4xl font-black" style={{ color: '#2A2118' }}>按分类查看商品</h2>
          <p className="text-base font-semibold mt-1" style={{ color: '#7A6E64' }}>카테고리별 상품</p>
          <p className="text-sm mt-1" style={{ color: '#5C4F42' }}>
            食品·生活用品·护肤·衣物香水·护手霜·运动鞋·健康用品全品类。
            <span className="block text-xs font-medium mt-0.5" style={{ color: '#8C7D6E' }}>식품·생활용품·스킨케어·섬유향수·핸드크림·운동화·건강용품 전 카테고리를 확인하세요.</span>
          </p>
        </div>

        {/* ── 상위 그룹 탭 바 ── */}
        <div className="relative mb-3">
          {canScrollLeft && (
            <button
              onClick={() => scrollTabs('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-150"
              style={{ backgroundColor: '#2E5240', color: '#fff' }}
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scrollTabs('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-150"
              style={{ backgroundColor: '#2E5240', color: '#fff' }}
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          )}

          <div
            ref={tabBarRef}
            className="flex gap-1 overflow-x-auto scrollbar-hide rounded-xl p-1.5"
            style={{
              backgroundColor: '#EDE8DF',
              border: '1px solid #D8CEBF',
              paddingLeft: canScrollLeft ? '2.5rem' : '0.375rem',
              paddingRight: canScrollRight ? '2.5rem' : '0.375rem',
            }}
          >
            {categoryGroups.map(group => {
              const isActive = activeGroup === group.id;
              const count = group.children
                ? group.children.reduce((acc, cat) => acc + products.filter(p => p.category === cat).length, 0)
                : products.filter(p => p.category === (group.id as Category)).length;

              return (
                <button
                  key={group.id}
                  onClick={() => handleGroupClick(group.id)}
                  className="flex-shrink-0 px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={
                    isActive
                      ? { backgroundColor: '#2E5240', color: '#fff' }
                      : { color: '#5C4F42', backgroundColor: 'transparent' }
                  }
                  onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(46,82,64,0.08)'; }}
                  onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                >
                  <span className="text-[13px] font-bold">{group.zh}</span>
                  <span className="text-[10px] ml-1" style={isActive ? { color: 'rgba(255,255,255,0.65)' } : { color: '#8C7D6E' }}>
                    {group.ko}
                  </span>
                  <span className="ml-1.5 text-xs" style={isActive ? { color: 'rgba(255,255,255,0.7)' } : { color: '#8C7D6E' }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── 식품 세부 카테고리 탭 ── */}
        {activeGroup === 'food' && (
          <div
            className="flex flex-wrap gap-2 mb-6 px-3 py-3 rounded-xl"
            style={{ backgroundColor: '#E8F0EB', border: '1px solid #C5D9CB' }}
          >
            <span className="text-[11px] font-bold self-center mr-1" style={{ color: '#2E5240' }}>
              细分类
              <span className="ml-1 font-normal" style={{ color: '#5C8A6E' }}>/ 세부 카테고리</span>
            </span>
            {FOOD_CATEGORIES.map(cat => {
              const isActive = activeFoodSub === cat;
              const count = products.filter(p => p.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => handleFoodSubClick(cat)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={
                    isActive
                      ? { backgroundColor: '#2E5240', color: '#fff' }
                      : { backgroundColor: '#fff', color: '#3D6B52', border: '1px solid #C5D9CB' }
                  }
                >
                  <span>{zhMap[cat]}</span>
                  <span className="text-[10px]" style={isActive ? { color: 'rgba(255,255,255,0.6)' } : { color: '#8C7D6E' }}>
                    {cat}
                  </span>
                  <span
                    className="ml-0.5 text-[10px] font-bold px-1 rounded-full"
                    style={
                      isActive
                        ? { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }
                        : { backgroundColor: '#E8F0EB', color: '#5C8A6E' }
                    }
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── 스킨케어·뷰티 세부 카테고리 탭 ── */}
        {activeGroup === '스킨케어·뷰티' && (
          <div
            className="flex flex-wrap gap-2 mb-6 px-3 py-3 rounded-xl"
            style={{ backgroundColor: '#F5EEF5', border: '1px solid #DDD0DD' }}
          >
            <span className="text-[11px] font-bold self-center mr-1" style={{ color: '#6B4E6B' }}>
              细分类
              <span className="ml-1 font-normal" style={{ color: '#9A7A9A' }}>/ 세부 카테고리</span>
            </span>
            {BEAUTY_CATEGORIES.map(cat => {
              const isActive = activeBeautySub === cat;
              const count = products.filter(p => p.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => handleBeautySubClick(cat)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={
                    isActive
                      ? { backgroundColor: '#2E5240', color: '#fff' }
                      : { backgroundColor: '#fff', color: '#6B4E6B', border: '1px solid #DDD0DD' }
                  }
                >
                  <span>{zhMap[cat]}</span>
                  <span className="text-[10px]" style={isActive ? { color: 'rgba(255,255,255,0.6)' } : { color: '#9A7A9A' }}>
                    {cat}
                  </span>
                  <span
                    className="ml-0.5 text-[10px] font-bold px-1 rounded-full"
                    style={
                      isActive
                        ? { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }
                        : { backgroundColor: '#F5EEF5', color: '#6B4E6B' }
                    }
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Category info bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 rounded-full" style={{ backgroundColor: '#C4985A' }}></div>
            {activeGroup === 'food' ? (
              <>
                <span className="font-bold text-base" style={{ color: '#2A2118' }}>
                  食品 &gt; {zhMap[activeFoodSub]}
                </span>
                <span className="text-sm" style={{ color: '#8C7D6E' }}>
                  共 {filtered.length} 个商品
                </span>
              </>
            ) : activeGroup === '스킨케어·뷰티' ? (
              <>
                <span className="font-bold text-base" style={{ color: '#2A2118' }}>
                  护肤·美妆 &gt; {zhMap[activeBeautySub]}
                </span>
                <span className="text-sm" style={{ color: '#8C7D6E' }}>
                  共 {filtered.length} 个商品
                </span>
              </>
            ) : (
              <>
                <span className="font-bold text-base" style={{ color: '#2A2118' }}>{activeSingleCat ? zhMap[activeSingleCat] || activeSingleCat : ''}</span>
                <span className="text-sm" style={{ color: '#8C7D6E' }}>共 {filtered.length} 个商品</span>
              </>
            )}
          </div>
          {activeGroup === 'food' && (
            <span className="text-xs" style={{ color: '#8C7D6E' }}>
              食품共{foodTotalCount}개
              <span className="ml-1" style={{ color: '#B0A090' }}>/ 식품 전체 {foodTotalCount}개</span>
            </span>
          )}
          {activeGroup === '스킨케어·뷰티' && (
            <span className="text-xs" style={{ color: '#8C7D6E' }}>
              美妆共{beautyTotalCount}개
              <span className="ml-1" style={{ color: '#B0A090' }}>/ 뷰티 전체 {beautyTotalCount}개</span>
            </span>
          )}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {displayed.map((product, i) => (
            <div
              key={`${currentCategory}-${product.id}-${i}`}
              className="animate-fadeIn"
              style={{ animationDelay: `${(i % 12) * 40}ms` }}
            >
              <ProductCard product={product} onInquiry={onInquiry} />
            </div>
          ))}
        </div>

        {/* 더보기 버튼 */}
        {hasMore && (
          <div className="text-center mt-10">
            <button
              onClick={() => setPage(p => p + 1)}
              className="inline-flex items-center gap-2 px-8 py-3 font-bold text-sm rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
              style={{ border: '1.5px solid #C4985A', color: '#C4985A', backgroundColor: 'transparent' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#C4985A'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#C4985A'; }}
            >
              <i className="ri-add-line"></i>
              查看更多 {Math.min(PAGE_SIZE, filtered.length - displayed.length)} 个
              <span className="text-xs opacity-60 ml-1.5">({displayed.length} / {filtered.length})</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
